import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva } from "class-variance-authority";
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-[200] bg-black/60 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
    data-ccuk-drawer-overlay="true" />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-[201] gap-4 bg-background p-6 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.35)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b rounded-b-2xl data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r rounded-r-2xl data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l rounded-l-2xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

// A zero-height sticky overlay keeps Close accessible without inserting a blank
// toolbar above banners or working content. Explicit 'row' is retained for
// any surface that genuinely needs a separate close toolbar.
const SheetContent = React.forwardRef(({ side = "right", className, children, showClose = true, closeLayout = "overlay", ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        sheetVariants({ side }),
        "overflow-y-auto overscroll-contain",
        className
      )}
      data-ccuk-sheet-content="true"
      data-ccuk-close-layout={showClose ? closeLayout : "none"}
      data-side={side}
      {...props}
    >
      {showClose && (
        <div
          className={cn(
            closeLayout === "overlay"
              ? "sticky top-0 z-40 flex h-0 min-h-0 items-start justify-end overflow-visible bg-transparent p-0 pointer-events-none"
              : "sticky top-0 z-30 mb-2 flex min-h-12 items-center justify-end bg-background/95 p-2 backdrop-blur"
          )}
          data-ccuk-sheet-close-row="true"
        >
          <SheetPrimitive.Close
            className={cn(
              "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-theme-muted outline-none ring-offset-background transition-[opacity,color,background-color,box-shadow] hover:text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] focus-visible:ring-offset-2 disabled:pointer-events-none",
              closeLayout === "overlay"
                ? "pointer-events-auto relative z-40 mr-3 mt-[max(0.75rem,env(safe-area-inset-top))] border border-theme bg-theme-card/95 shadow-md backdrop-blur hover:bg-theme-secondary"
                : "opacity-80 hover:opacity-100 hover:bg-theme/10"
            )}
            data-ccuk-sheet-close="true"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        </div>
      )}
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-left", className)}
    data-ccuk-sheet-header="true"
    {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
